// Shared contract for the Admin "Add Photos" handoff.  This module is pure so
// the UI can keep a photo session in component state without uploading a
// selected image before Edit Bio has actually been saved.

// "blob:" = a browser file picked in the web app (web/src/lib/files.js).
const LOCAL_URI_PREFIXES = ["file:", "content:", "data:", "ph:", "blob:"];

function requirePathSegment(value, label) {
  if (
    typeof value !== "string" ||
    !value.trim() ||
    value.includes("/") ||
    value.includes("\\")
  ) {
    throw new Error(`${label} must be one Storage path segment`);
  }
  return value.trim();
}

function fnv1a(value) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function normalizeUri(uri) {
  if (typeof uri !== "string" || !uri.trim()) return "";
  // Picker query/hash fragments are not part of the selected file identity.
  // Keep the scheme and path intact: local URIs can legitimately contain
  // spaces, so do not attempt to turn them into a Storage path.
  return uri.trim().replace(/\\/g, "/").replace(/[?#].*$/, "");
}

function normalizedFileName(asset, uri) {
  const suppliedName = asset?.fileName || asset?.filename || "";
  const source = String(suppliedName || uri || "");
  const fileName = source.split("/").pop() || "image";
  return fileName.trim().toLowerCase();
}

function normalizedDimension(value) {
  return Number.isFinite(Number(value)) ? Math.round(Number(value)) : "unknown";
}

function uniqueStrings(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : []).filter((value) => {
    if (typeof value !== "string" || !value.trim() || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

/**
 * Creates one session per Edit Bio/photo-picker handoff.  The random suffix is
 * generated once, not per upload attempt; callers must keep it in state until
 * the session is saved or cancelled.
 */
export function createPhotoSessionId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `photo-${Date.now().toString(36)}-${randomPart}`;
}

/**
 * Uses the picker asset ID where available.  Older picker results do not have
 * one, so their stable fallback is normalized URI + filename + dimensions.
 */
function getPhotoAssetIdentity(asset) {
  const assetId = asset?.assetId || asset?.id;
  if (typeof assetId === "string" && assetId.trim()) {
    return `asset:${assetId.trim()}`;
  }

  const sourceUri = normalizeUri(asset?.sourceUri || asset?.uri || asset?.uploadUri);
  if (!sourceUri) throw new Error("A selected photo needs a local URI");

  return [
    "picker",
    sourceUri,
    normalizedFileName(asset, sourceUri),
    `${normalizedDimension(asset?.width)}x${normalizedDimension(asset?.height)}`,
  ].join("|");
}

/**
 * Immutable object key for a selected asset.  It deliberately has no retry
 * counter, timestamp, or selection index, which would create duplicate files
 * when a request is repeated.
 */
function getPhotoObjectKey({ sessionId, assetIdentity }) {
  const safeSessionId = requirePathSegment(sessionId, "sessionId");
  if (typeof assetIdentity !== "string" || !assetIdentity) {
    throw new Error("assetIdentity is required");
  }
  return `photo-${safeSessionId}-${fnv1a(assetIdentity)}.jpg`;
}

function getClubGalleryStoragePath({ uid, clubId, objectKey }) {
  return [
    "club-gallery",
    requirePathSegment(clubId, "clubId"),
    requirePathSegment(uid, "uid"),
    requirePathSegment(objectKey, "objectKey"),
  ].join("/");
}

/**
 * Converts ImagePicker/compression results to retryable records.  The original
 * picker URI establishes identity; uploadUri may be replaced by a compressed
 * local URI without changing the object key.
 */
export function stagePhotoAssets({ uid, clubId, sessionId, assets }) {
  const seen = new Set();
  return (Array.isArray(assets) ? assets : []).reduce((staged, rawAsset) => {
    const asset = typeof rawAsset === "string" ? { uri: rawAsset } : rawAsset || {};
    const assetIdentity = getPhotoAssetIdentity(asset);
    if (seen.has(assetIdentity)) return staged;
    seen.add(assetIdentity);

    const sourceUri = asset.sourceUri || asset.uri || asset.uploadUri;
    const uploadUri = asset.uploadUri || asset.compressedUri || sourceUri;
    const objectKey = getPhotoObjectKey({ sessionId, assetIdentity });
    staged.push({
      assetId: typeof asset.assetId === "string" ? asset.assetId : undefined,
      assetIdentity,
      sessionId,
      sourceUri,
      uploadUri,
      fileName: normalizedFileName(asset, sourceUri),
      width: normalizedDimension(asset.width),
      height: normalizedDimension(asset.height),
      objectKey,
      storagePath: getClubGalleryStoragePath({ uid, clubId, objectKey }),
      status: "selected",
      url: null,
      error: null,
      cleanupStatus: null,
    });
    return staged;
  }, []);
}

function isPersistedGalleryUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  const lowerCaseValue = value.trim().toLowerCase();
  return !LOCAL_URI_PREFIXES.some((prefix) => lowerCaseValue.startsWith(prefix));
}

/** Preserve saved remote URLs while adding each successful session upload once. */
export function mergeGalleryUrls(savedGallery, stagedAssets) {
  const uploadedUrls = (Array.isArray(stagedAssets) ? stagedAssets : [])
    .filter((asset) => asset?.status === "uploaded" && isPersistedGalleryUrl(asset.url))
    .map((asset) => asset.url);
  return uniqueStrings([
    ...(Array.isArray(savedGallery) ? savedGallery.filter(isPersistedGalleryUrl) : []),
    ...uploadedUrls,
  ]);
}

/** Applies only matching settled records, preventing an old response from changing another asset. */
export function applySettledUploadResults(stagedAssets, settledResults) {
  const byIdentity = new Map(
    (Array.isArray(settledResults) ? settledResults : []).map((result) => [
      result?.assetIdentity,
      result,
    ])
  );

  return (Array.isArray(stagedAssets) ? stagedAssets : []).map((asset) => {
    const result = byIdentity.get(asset.assetIdentity);
    if (!result || result.objectKey !== asset.objectKey || result.storagePath !== asset.storagePath) {
      return asset;
    }
    if (result.status === "uploaded") {
      return { ...asset, status: "uploaded", url: result.url, error: null, cleanupStatus: null };
    }
    if (result.status === "failed") {
      return { ...asset, status: "failed", error: result.error || "Upload failed" };
    }
    return asset;
  });
}

export function getRetryablePhotoAssets(stagedAssets) {
  return (Array.isArray(stagedAssets) ? stagedAssets : []).filter(
    (asset) => asset?.status === "failed"
  );
}

/** A removed upload remains in the ledger so its exact object path can be cleaned up. */
export function removeStagedPhotoAsset(stagedAssets, assetIdentity) {
  return (Array.isArray(stagedAssets) ? stagedAssets : []).map((asset) =>
    asset.assetIdentity === assetIdentity
      ? {
          ...asset,
          status: asset.status === "uploaded" ? "cleanup-pending" : "removed",
        }
      : asset
  );
}

/** Cancel leaves uploaded paths discoverable for cleanup but never alters saved gallery data. */
export function cancelPhotoSession(stagedAssets) {
  return (Array.isArray(stagedAssets) ? stagedAssets : []).map((asset) => ({
    ...asset,
    status: asset.status === "uploaded" ? "cleanup-pending" : "cancelled",
  }));
}

export function getSessionUploadsForCleanup(stagedAssets) {
  return (Array.isArray(stagedAssets) ? stagedAssets : [])
    .filter((asset) => asset?.url && asset?.storagePath && ["uploaded", "cleanup-pending"].includes(asset.status))
    .map(({ assetIdentity, objectKey, storagePath, url, sessionId }) => ({
      assetIdentity,
      objectKey,
      storagePath,
      url,
      sessionId,
    }));
}
